import React, {
  useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { debounce } from 'lodash';
import ReactDOM from 'react-dom';
import { Editor, EditorState, RichUtils } from 'draft-js';
import shallow from 'zustand/shallow';
import { useStore } from '../../store/store';
import { useStyleMap } from '../../hooks';
import { decorator, getBlockStyle, handleBeforeInput } from '../RichEditor/utils';
import ContextualMenu from '../ContextualMenu/ContextualMenu';
import TextStylingComponent, { imposedInlineStyles } from '../TextStylingComponent/TextStylingComponent';
import FloatyTextStyling from '../Table/FloatyTextStyling';
import { EditorProvider } from '../../contexts/EditorContext';
import { MENU_ANCESTORS } from '../../constants/constants';
import { mergeConfigurationColors } from '../../utils/styles';

/**
 * Un texte riche d'une box qui en porte PLUSIEURS (Volet, Carte info…).
 *
 * `RichEditor` est câblé sur `box.content.editorState` — un seul texte par
 * box, avec sa barre d'outils, ses styles de texte et sa mesure de hauteur.
 * Ici chaque texte adresse sa propre clé (`content.<clé>EditorState`) et vit
 * à côté de ses voisins.
 *
 * Ce qu'il porte : la frappe, les raccourcis Draft, le décorateur commun (donc
 * le rendu des variables), et — pour le seul texte qui a le focus — le menu
 * d'insertion de variable et la barre de mise en forme (panel de droite et
 * barre flottante à la sélection). Le style de départ vient du style de volet
 * du template ; la barre sert à en déroger mot à mot.
 *
 * Ce qu'il ne porte pas, volontairement : les styles de texte du proposal
 * (ils appartiennent aux box texte) et le relais collab de la frappe
 * (`patchBoxContent` n'adresse qu'un texte par box) — le verrou de box, lui,
 * s'applique normalement.
 */
const BoxDraftText = ({
  /** Expose `flush()` : écrit tout de suite dans la box ce qui est à l'écran. */
  ref,
  box,
  contentKey,
  isSelected,
  placeholder = '',
  style,
  /** Label de palette de la couleur de départ, pour la barre de mise en forme. */
  defaultColorLabel = '',
  /**
   * Ce texte est-il celui que les barres de la box pilotent ? Piloté par le
   * parent (dernier texte à avoir pris le focus) et NON par le focus local :
   * cliquer un bouton de la barre retire le focus à l'éditeur, ce qui
   * démonterait la barre sous le curseur.
   */
  isActive = true,
  /**
   * Alignement du texte. Draft pose `text-align: left` sur chaque bloc via sa
   * classe `public-DraftStyleDefault-ltr` : un `textAlign` sur un parent ne
   * traverse pas, il faut passer par la prop `textAlignment` de l'éditeur.
   */
  textAlign = 'left',
  /** Position des sections de mise en forme dans le panel de droite. */
  panelOrder = 0,
  onFocusChange,
  /**
   * Où vit le texte dans la box, quand ce n'est pas `content.<clé>EditorState`
   * (texte d'une sous-forme ou d'un lien de forme composée) : `readState(box)`
   * le lit, `writeState(draftBox, editorState)` l'écrit dans le brouillon.
   */
  readState,
  writeState,
}) => {
  const editorRef = useRef(null);
  const styleMap = useStyleMap();
  const { configuration, tmp } = useStore(
    ({ configuration, tmp }) => ({ configuration, tmp }),
    shallow
  );
  const updateBox = useStore((state) => state.updateBox);
  const setBoxMovable = useStore((state) => state.setBoxMovable);
  const setEventManagerEnabled = useStore((state) => state.setEventManagerEnabled);
  const documentContentSyncTick = useStore((state) => state.documentContentSyncTick);
  const consumeSkipBoxSaveOnUnselect = useStore((state) => state.consumeSkipBoxSaveOnUnselect);
  // Gras / italique / souligné du texte de départ (question de volet, titre de
  // carte) : la barre les montre actifs.
  const defaultInlineStyles = imposedInlineStyles(style);

  const stateKey = `${contentKey}EditorState`;
  const readStored = useCallback(
    (source) => (readState ? readState(source) : source.content?.[stateKey]),
    [readState, stateKey]
  );
  const storedEditorState = readStored(box);

  const [editorState, setEditorState] = useState(() => EditorState.createWithContent(
    (storedEditorState || EditorState.createEmpty()).getCurrentContent(),
    decorator
  ));
  const [defaultSelection, setDefaultSelection] = useState(null);
  /* La barre flottante suit le FOCUS, pas la sélection de la box : sans ça
     elle restait affichée après un clic ailleurs, tant que la box était
     sélectionnée. Cliquer un bouton de la barre ne la fait pas disparaître —
     FloatyStylingWrapper annule le mousedown, donc l'éditeur garde le focus. */
  const [hasFocus, setHasFocus] = useState(false);

  // Dernier contenu que l'éditeur a lui-même écrit dans la box, ou chargé
  // depuis elle : un contenu du store qui en diffère vient d'ailleurs.
  const knownContentRef = useRef(storedEditorState?.getCurrentContent() ?? null);

  const saveImmediate = useCallback((nextEditorState) => {
    const nextContent = nextEditorState.getCurrentContent();
    knownContentRef.current = nextContent;
    updateBox(box.id, (draftBox) => {
      // Contenu inchangé : ne pas forger un nouvel EditorState, sinon chaque
      // désélection produit une nouvelle référence de box — un diff fantôme
      // dans le do/undo (cf. RichEditor).
      if (readStored(draftBox)?.getCurrentContent() === nextContent) return;
      const nextState = EditorState.createWithContent(nextContent, decorator);
      if (writeState) writeState(draftBox, nextState);
      else draftBox.content[stateKey] = nextState;
    });
  }, [box.id, stateKey, updateBox, readStored, writeState]);

  // Écriture pendant la frappe, débouncée. Les box texte s'en passent : leur
  // frappe part au serveur par le brouillon collab (`patchBoxContent`), qui
  // n'adresse qu'un texte par box et ne sert donc à rien ici. Sans ça, taper
  // une question puis enregistrer ou générer le site sans avoir désélectionné
  // la box envoyait l'ancien contenu.
  const saveDebounced = useMemo(
    () => debounce((nextEditorState) => saveImmediate(nextEditorState), 400),
    [saveImmediate]
  );
  useEffect(() => () => saveDebounced.flush(), [saveDebounced]);

  // Accepte une valeur OU une fonction de mise à jour : le menu d'insertion de
  // variable appelle `setEditorState((prev) => ...)` pour lire l'état courant.
  const write = useCallback((next) => {
    setEditorState((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      saveDebounced(resolved);
      return resolved;
    });
  }, [saveDebounced]);

  // Écriture au moment où la box cesse d'être sélectionnée, comme les titres.
  const cleanupRef = useRef(editorState);
  useEffect(() => {
    cleanupRef.current = editorState;
  });
  useLayoutEffect(() => () => {
    if (isSelected && cleanupRef.current) {
      if (consumeSkipBoxSaveOnUnselect(box.id)) return;
      saveImmediate(cleanupRef.current);
    }
  }, [isSelected, saveImmediate, box.id, consumeSkipBoxSaveOnUnselect]);

  // Le parent écrit tout de suite ce qui est à l'écran avant de réécrire le
  // texte lui-même (modèle de Carte info) : il doit voir la frappe des
  // dernières 400 ms, sinon elle passerait pour l'exemple qu'elle remplace.
  useImperativeHandle(ref, () => ({
    flush: () => {
      saveDebounced.cancel();
      if (cleanupRef.current.getCurrentContent() === knownContentRef.current) return;
      saveImmediate(cleanupRef.current);
    },
  }), [saveDebounced, saveImmediate]);

  // Contenu réécrit ailleurs (chargement, undo, modèle de carte appliqué
  // depuis le panel) : rechargé, box sélectionnée ou non. Box sélectionnée,
  // seul ce que l'éditeur a lui-même écrit est ignoré — la frappe a pu avancer
  // depuis l'écriture débouncée, la recharger l'effacerait. Ne recharger
  // qu'hors sélection laissait le texte d'un modèle invisible, puis l'ancien
  // texte l'écrasait à la désélection.
  useEffect(() => {
    if (!storedEditorState) return;
    const storedContent = storedEditorState.getCurrentContent();
    if (storedContent === cleanupRef.current.getCurrentContent()) return;
    if (isSelected && storedContent === knownContentRef.current) return;
    // L'écriture débouncée en attente porte l'ancien texte : partie après
    // coup, elle écraserait celui qui vient d'arriver.
    saveDebounced.cancel();
    knownContentRef.current = storedContent;
    setEditorState(EditorState.createWithContent(storedContent, decorator));
  }, [storedEditorState, isSelected, saveDebounced]);

  useEffect(() => {
    if (storedEditorState) {
      setEditorState(EditorState.createWithContent(
        storedEditorState.getCurrentContent(),
        decorator
      ));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentContentSyncTick, box.id]);

  return (
    <EditorProvider
      setEditorState={setEditorState}
      editorState={editorState}
      readOnly={!isSelected}
      boxId={box.id}
    >
      <div
        style={style}
        onClick={() => {
          if (isSelected && !editorState.getSelection().getHasFocus()) {
            editorRef.current?.focus();
          }
        }}
        onKeyDown={(e) => {
          // Sans ça, les raccourcis du document (flèches, suppression) se
          // déclencheraient pendant la frappe.
          if (editorState.getSelection().getHasFocus()) e.stopPropagation();
        }}
      >
        <Editor
          stripPastedStyles
          // Sans la table de styles, la barre de mise en forme pose bien les
          // styles inline dans l'état Draft mais rien ne les rend : couleur,
          // taille et police restaient sans effet.
          customStyleMap={styleMap}
          blockStyleFn={getBlockStyle}
          textAlignment={textAlign}
          handleBeforeInput={(chars, currentState) => handleBeforeInput({
            chars,
            editorState: currentState,
            setEditorState,
          })}
          // Gras / italique / souligné au clavier, comme partout ailleurs.
          handleKeyCommand={(command, currentState) => {
            const next = RichUtils.handleKeyCommand(currentState, command);
            if (!next) return 'not-handled';
            setEditorState(next);
            return 'handled';
          }}
          placeholder={placeholder}
          editorState={editorState}
          onChange={write}
          spellCheck
          ref={editorRef}
          readOnly={!isSelected}
          onFocus={() => {
            setBoxMovable(false);
            setEventManagerEnabled(false);
            setHasFocus(true);
            onFocusChange?.(contentKey);
          }}
          onBlur={() => setHasFocus(false)}
        />
      </div>
      {/* Mise en forme du texte : panel de droite + barre flottante à la
          sélection. Pas de `textStyle` : un volet n'a pas de style de texte du
          proposal, seulement des couleurs de départ posées par son style de
          volet. */}
      {isSelected && isActive && (
        <>
          <TextStylingComponent
            boxId={box.id}
            updateBox={updateBox}
            editorState={editorState}
            setEditorState={write}
            updateBoxOnStylingChange={() => null}
            setDefaultSelection={setDefaultSelection}
            colors={mergeConfigurationColors(configuration, tmp)}
            defaultFontSize={configuration.fontSize}
            defaultFontFamily={configuration.fontFamily}
            defaultColor={defaultColorLabel || configuration.baseFontColor?.label}
            defaultInlineStyles={defaultInlineStyles}
            hasVariables
            wrap
            panelOrder={panelOrder}
          />
          {hasFocus && ReactDOM.createPortal(
            <FloatyTextStyling
              editorState={editorState}
              setEditorState={write}
              boxId={box.id}
              updateBox={updateBox}
              colors={mergeConfigurationColors(configuration, tmp)}
              defaultFontSize={configuration.fontSize}
              defaultFontFamily={configuration.fontFamily}
              defaultColor={defaultColorLabel || configuration.baseFontColor?.label}
              defaultInlineStyles={defaultInlineStyles}
            />,
            document.getElementById('SelectionVariablePortal')
          )}
        </>
      )}
      {/* Menu d'insertion de variable : un seul texte le monte — deux menus
          dans le même portail se recouvriraient. */}
      {isSelected && isActive && ReactDOM.createPortal(
        <ContextualMenu
          editorState={editorState}
          setEditorState={write}
          menuAncestorType={MENU_ANCESTORS.BOX.content}
          menuAncestorIds={[box.id]}
          defaultSelection={defaultSelection}
          setDefaultSelection={setDefaultSelection}
        />,
        document.getElementById('SelectionVariablePortal')
      )}
    </EditorProvider>
  );
};

export default BoxDraftText;
